import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  Divider,
  Typography,
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

const getDisplayNameParts = (user) => {
  const firstName = user?.firstName || user?.name?.split(' ')[0] || '';
  const lastName = user?.lastName || user?.name?.split(' ').slice(1).join(' ') || '';

  return { firstName, lastName };
};

const EditProfileDialog = ({ open, onClose }) => {
  const { t } = useTranslation(['common']);
  const { user, updateUserProfile } = useAuth();
  const { getCurrencySymbol } = useCurrency();
  const initialNameParts = getDisplayNameParts(user);
  
  const [formData, setFormData] = useState({
    firstName: initialNameParts.firstName,
    lastName: initialNameParts.lastName,
    email: user?.email || '',
    phone: user?.phone || '',
    // Champs spécifiques au locataire
    isTenant: user?.isTenant || false,
    occupation: user?.tenantProfile?.occupation || '',
    maxBudget: user?.tenantProfile?.maxBudget || '',
    preferredLocation: user?.tenantProfile?.preferredLocation || '',
    // Champs spécifiques au propriétaire
    isOwner: user?.isOwner || false,
    companyName: user?.ownerProfile?.companyName || '',
    taxIdentificationNumber: user?.ownerProfile?.taxIdentificationNumber || '',
    yearsOfExperience: user?.ownerProfile?.yearsOfExperience || ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [phoneCheckLoading, setPhoneCheckLoading] = useState(false);
  const phoneCheckTimeoutRef = React.useRef(null);

  // Nettoyer le timeout quand le dialog se ferme
  React.useEffect(() => {
    return () => {
      if (phoneCheckTimeoutRef.current) {
        clearTimeout(phoneCheckTimeoutRef.current);
      }
    };
  }, []);
  
  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Si c'est le champ phone, vérifier l'unicité avec debounce
    if (name === 'phone') {
      checkPhoneAvailability(value);
    }
  };

  // Vérifier l'unicité du phone avec debounce
  const checkPhoneAvailability = async (phoneValue) => {
    // Effacer le timeout précédent
    if (phoneCheckTimeoutRef.current) {
      clearTimeout(phoneCheckTimeoutRef.current);
    }

    // Si le champ est vide, c'est ok
    if (!phoneValue || phoneValue.trim() === '') {
      setPhoneError('');
      return;
    }

    // Mettre en place un debounce de 500ms
    setPhoneCheckLoading(true);
    phoneCheckTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await api.get('/auth/check-phone-availability', {
          params: {
            phone: phoneValue,
            excludeUserId: user?.id
          }
        });

        if (response.data.available === false) {
          setPhoneError(response.data.message || t('editProfile.phoneAlreadyExists', 'Ce numéro de téléphone est déjà utilisé.'));
        } else {
          setPhoneError('');
        }
      } catch (err) {
        console.error('Erreur lors de la vérification du phone:', err);
        // Ne pas afficher d'erreur si l'API échoue, laisser le backend décider
        setPhoneError('');
      } finally {
        setPhoneCheckLoading(false);
      }
    }, 500);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Vérifier s'il y a une erreur sur le phone
    if (phoneError) {
      setError(t('editProfile.phoneValidationError', 'Veuillez corriger l\'erreur du numéro de téléphone avant de continuer.'));
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(false);
    
    try {
      // Préparer les données à envoyer
      const profileData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone?.trim() || null, // Convertir chaînes vides en NULL
        isTenant: formData.isTenant,
        isOwner: formData.isOwner,
        tenantProfile: formData.isTenant ? {
          occupation: formData.occupation,
          maxBudget: formData.maxBudget ? parseFloat(formData.maxBudget) : null,
          preferredLocation: formData.preferredLocation
        } : null,
        ownerProfile: formData.isOwner ? {
          companyName: formData.companyName,
          taxIdentificationNumber: formData.taxIdentificationNumber,
          yearsOfExperience: formData.yearsOfExperience ? parseInt(formData.yearsOfExperience, 10) : null
        } : null
      };
      
      // Appeler la fonction de mise à jour du profil
      const result = await updateUserProfile(profileData);
      
      if (result.success) {
        setSuccess(true);
        // Fermer la boîte de dialogue après un court délai
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setError(result.message || 'Une erreur est survenue lors de la mise à jour du profil.');
      }
    } catch (err) {
      console.error('Erreur lors de la mise à jour du profil:', err);
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
            <Dialog 
          open={open} 
          onClose={loading ? undefined : onClose}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>{t('editProfile.title')}</DialogTitle>
          
          <form onSubmit={handleSubmit}>
            <DialogContent>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              
              {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {t('editProfile.successMessage')}
                </Alert>
              )}
              
              <Typography variant="h6" gutterBottom>
                {t('editProfile.generalInformation')}
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('common:profile.firstName')}
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    fullWidth
                    required
                    margin="normal"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('common:profile.lastName')}
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    fullWidth
                    required
                    margin="normal"
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('editProfile.email')}
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    fullWidth
                    required
                    margin="normal"
                    disabled={true}
                    sx={{
                      '& .MuiOutlinedInput-root.Mui-disabled': {
                        backgroundColor: '#f5f5f5',
                        color: '#999'
                      }
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('editProfile.phone')}
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    error={Boolean(phoneError)}
                    helperText={phoneError || (phoneCheckLoading ? t('editProfile.checkingPhone', 'Vérification...') : '')}
                    disabled={loading}
                    InputProps={{
                      endAdornment: phoneCheckLoading ? (
                        <CircularProgress color="inherit" size={20} sx={{ mr: 1 }} />
                      ) : null
                    }}
                  />
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 3, mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isTenant}
                      onChange={handleChange}
                      name="isTenant"
                      color="primary"
                    />
                  }
                  label={t('editProfile.tenantLabel')}
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isOwner}
                      onChange={handleChange}
                      name="isOwner"
                      color="secondary"
                    />
                  }
                  label={t('editProfile.ownerLabel')}
                />
              </Box>
              
              {formData.isTenant && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {t('editProfile.tenantInfo')}
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        label={t('editProfile.profession')}
                        name="occupation"
                        value={formData.occupation}
                        onChange={handleChange}
                        fullWidth
                        margin="normal"
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        label={t('editProfile.maxBudget', { currency: getCurrencySymbol() })}
                        name="maxBudget"
                        type="number"
                        value={formData.maxBudget}
                        onChange={handleChange}
                        fullWidth
                        margin="normal"
                        InputProps={{ 
                          inputProps: { min: 0 },
                          startAdornment: getCurrencySymbol()
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        label={t('editProfile.preferredLocation')}
                        name="preferredLocation"
                        value={formData.preferredLocation}
                        onChange={handleChange}
                        fullWidth
                        margin="normal"
                      />
                    </Grid>
                  </Grid>
                </>
              )}
              
              {formData.isOwner && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {t('editProfile.ownerInfo')}
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label={t('editProfile.companyName')}
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        fullWidth
                        margin="normal"
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        label={t('editProfile.taxNumber')}
                        name="taxIdentificationNumber"
                        value={formData.taxIdentificationNumber}
                        onChange={handleChange}
                        fullWidth
                        margin="normal"
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <TextField
                        label={t('editProfile.yearsExperience')}
                        name="yearsOfExperience"
                        type="number"
                        value={formData.yearsOfExperience}
                        onChange={handleChange}
                        fullWidth
                        margin="normal"
                        InputProps={{ inputProps: { min: 0 } }}
                      />
                    </Grid>
                  </Grid>
                </>
              )}
            </DialogContent>
            
            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button 
                onClick={onClose} 
                disabled={loading}
              >
                {t('editProfile.cancel')}
              </Button>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                disabled={loading || Boolean(phoneError)}
                startIcon={loading && <CircularProgress size={20} color="inherit" />}
              >
                {loading ? t('editProfile.saving') : t('editProfile.save')}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
  );
};

export default EditProfileDialog;